import React, { useRef, useState } from "react";
import { uploadLogo, updateBrand, deleteLogo, logoUrl } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Trash2, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const PRESETS = ["#002FA7", "#0F172A", "#0EA5E9", "#059669", "#B91C1C", "#7C2D12", "#4338CA"];

const BrandSettings = ({ open, onOpenChange, brand, onSaved }) => {
  const [accent, setAccent] = useState(brand.accent_color || "#002FA7");
  const [footer, setFooter] = useState(brand.footer_text || "Confidential Internal Document");
  const [hasLogo, setHasLogo] = useState(brand.has_logo);
  const [logoVersion, setLogoVersion] = useState(Date.now());
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  React.useEffect(() => {
    if (open) {
      setAccent(brand.accent_color || "#002FA7");
      setFooter(brand.footer_text || "Confidential Internal Document");
      setHasLogo(brand.has_logo);
      setLogoVersion(Date.now());
    }
  }, [open, brand]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await uploadLogo(fd);
      setHasLogo(true);
      setLogoVersion(Date.now());
      toast.success("Logo uploaded");
      onSaved?.();
    } catch (err) {
      toast.error("Logo upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await deleteLogo();
      setHasLogo(false);
      toast.success("Logo removed");
      onSaved?.();
    } catch (err) {
      toast.error("Could not remove logo");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateBrand({ accent_color: accent, footer_text: footer });
      toast.success("Brand settings saved");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid="brand-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Brand Settings</DialogTitle>
          <DialogDescription>Upload your logo, set an accent color and footer text applied to every document and export.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          {/* Logo */}
          <div>
            <Label className="label-caps">Company Logo</Label>
            <div className="mt-2 flex items-center gap-3">
              <div className="h-16 w-32 border border-slate-200 rounded-md bg-slate-900 flex items-center justify-center overflow-hidden shrink-0">
                {hasLogo ? (
                  <img src={`${logoUrl()}?v=${logoVersion}`} alt="logo" className="max-h-full max-w-full object-contain" data-testid="brand-logo-preview" />
                ) : (
                  <span className="text-[10px] text-slate-400">No logo</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="logo-file-input" />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2" data-testid="upload-logo-btn">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {hasLogo ? "Replace" : "Upload"}
                </Button>
                {hasLogo && (
                  <Button size="sm" variant="ghost" onClick={handleRemoveLogo} className="gap-2 text-red-600 hover:text-red-700" data-testid="remove-logo-btn">
                    <Trash2 size={14} /> Remove
                  </Button>
                )}
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">PNG or JPG. Shown in document header, footer credit line, and exports.</p>
          </div>

          {/* Accent color */}
          <div>
            <Label className="label-caps">Accent Color</Label>
            <div className="mt-2 flex items-center gap-2">
              <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="h-9 w-12 rounded border border-slate-200 cursor-pointer" data-testid="accent-color-picker" />
              <Input value={accent} onChange={(e) => setAccent(e.target.value)} className="w-28 font-mono text-sm" data-testid="accent-color-input" />
              <div className="flex gap-1.5 ml-1">
                {PRESETS.map((c) => (
                  <button key={c} onClick={() => setAccent(c)} style={{ background: c }} className="h-6 w-6 rounded-full border border-slate-200 hover:scale-110 transition-transform flex items-center justify-center" data-testid={`preset-${c}`}>
                    {accent.toLowerCase() === c.toLowerCase() && <Check size={12} className="text-white" />}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Footer text */}
          <div>
            <Label className="label-caps">Footer Text</Label>
            <Input value={footer} onChange={(e) => setFooter(e.target.value)} className="mt-2 text-sm" placeholder="Confidential Internal Document" data-testid="footer-text-input" />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="brand-cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2" style={{ background: accent }} data-testid="brand-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save Settings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BrandSettings;
